import { useClerk } from '@clerk/clerk-react';
import { lazy, type ReactElement, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppBootstrapController } from '@/app/AppBootstrapController';
import {
  shouldEnableAppShellBootstrap,
  shouldQueryAppShellEntitlements,
} from '@/app/appShellDataGuards';
import { AppShellRouter } from '@/app/AppShellRouter';
import { mergeShellProjects, resolveShellVisibleProjects } from '@/app/shellProjects';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { resolveSettingsWorkspaceId } from '@/features/organisations/utils/organisationSettingsRoute';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { useUpdateUserContext } from '@/features/users';
import { getToastMessage } from '@/shared/copy/toasts';
import {
  FeatureFlagsProvider,
  useAppDialogs,
  useAppFeatureFlags,
  useClientActivityMonitor,
  useDocumentVisibility,
  useOrganisationEntitlements,
} from '@/shared/hooks';
import { resolvePlanStatus } from '@/shared/plans/planStatus';
import { getPlanDisplayName, PLAN_PRICE_GBP_MINOR_BY_CODE } from '@flowqa/shared';
import { getPendingPostOnboardingState, getStoredContext } from './auth/sessionStorage';
import { useAuth } from './auth/useAuth';
import { getNavItemsFromRoutes } from './navigation/routes/nav';
import { appRouteDefinitions } from './navigation/routes/routes';
import { useNotifications } from './providers/notificationHooks';
import { isEntryAuthFlowPath } from './shared/hooks/useAppAuthBootstrapFlow';
import { useBootstrapData } from './shared/hooks/useBootstrapData';
import { useCurrentOrgRole } from './shared/hooks/useCurrentOrgRole';
import { useNavItems } from './shared/hooks/useNavItems';
import type { OrganisationDto } from '@flowqa/shared';

type OrganisationResolutionSession = {
  orgId?: string | null;
  lastOrganisationId?: string | null;
} | null;

type OrganisationResolutionInput = {
  selectedOrganisationId: string | null;
  session: OrganisationResolutionSession;
  accessibleOrganisationIds: Set<string>;
};

type RouteScopedWorkspaceSelectionInput = {
  pathname: string;
  search: string;
  selectedOrganisationId: string | null;
};

type PendingOnboardingSelectionInput = {
  pendingPostOnboardingOrganisationId: string | null;
  selectedOrganisationId: string | null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const resolveAccessScopedOrganisationId = ({
  selectedOrganisationId,
  session,
  accessibleOrganisationIds,
}: OrganisationResolutionInput): string | null => {
  const selectedId =
    selectedOrganisationId !== null && accessibleOrganisationIds.has(selectedOrganisationId)
      ? selectedOrganisationId
      : null;
  if (selectedId !== null) {
    return selectedId;
  }
  const sessionOrganisationId = session?.orgId ?? null;
  return sessionOrganisationId !== null && accessibleOrganisationIds.has(sessionOrganisationId)
    ? sessionOrganisationId
    : null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const resolveDisplayScopedOrganisationId = ({
  selectedOrganisationId,
  session,
  accessibleOrganisationIds,
}: OrganisationResolutionInput): string | null => {
  const accessScopedOrganisationId = resolveAccessScopedOrganisationId({
    selectedOrganisationId,
    session,
    accessibleOrganisationIds,
  });
  if (accessScopedOrganisationId !== null) {
    return accessScopedOrganisationId;
  }
  const sessionLastOrganisationId = session?.lastOrganisationId ?? null;
  return sessionLastOrganisationId !== null &&
    accessibleOrganisationIds.has(sessionLastOrganisationId)
    ? sessionLastOrganisationId
    : null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const shouldPreserveRouteScopedWorkspaceSelection = ({
  pathname,
  search,
  selectedOrganisationId,
}: RouteScopedWorkspaceSelectionInput): boolean => {
  if (pathname !== '/workspace-settings') {
    return false;
  }
  const routeWorkspaceId = resolveSettingsWorkspaceId(search);
  return routeWorkspaceId !== null && routeWorkspaceId === selectedOrganisationId;
};

// eslint-disable-next-line react-refresh/only-export-components
export const shouldPreservePendingOnboardingSelection = ({
  pendingPostOnboardingOrganisationId,
  selectedOrganisationId,
}: PendingOnboardingSelectionInput): boolean =>
  pendingPostOnboardingOrganisationId !== null &&
  selectedOrganisationId !== null &&
  pendingPostOnboardingOrganisationId === selectedOrganisationId;

type ClerkOrganisationSyncProps = {
  organisations: Array<Pick<OrganisationDto, 'id' | 'clerkOrganizationId'>>;
  displayOrganisationId: string | null;
};

const ClerkOrganisationSync = ({
  organisations,
  displayOrganisationId,
}: ClerkOrganisationSyncProps): null => {
  const clerk = useClerk();
  const activeClerkOrganisationId = clerk.organization?.id ?? null;

  useEffect(() => {
    if (!clerk.loaded) {
      return;
    }
    const selectedOrganisation =
      displayOrganisationId === null
        ? null
        : (organisations.find((organisation) => organisation.id === displayOrganisationId) ?? null);
    const targetClerkOrganisationId = selectedOrganisation?.clerkOrganizationId ?? null;
    if (displayOrganisationId !== null && targetClerkOrganisationId === null) {
      return;
    }
    if (targetClerkOrganisationId === activeClerkOrganisationId) {
      return;
    }
    void clerk.setActive({ organization: targetClerkOrganisationId }).catch(() => {
      // Keep local org selection stable even if Clerk active-org sync fails transiently.
    });
  }, [activeClerkOrganisationId, clerk, displayOrganisationId, organisations]);

  return null;
};

const AppDialogHost = lazy(async () => {
  const module = await import('@/app/AppDialogHost');
  return { default: module.AppDialogHost };
});

type AppProps = {
  authBypassClerk: boolean;
};

export const App = ({ authBypassClerk }: AppProps): ReactElement => {
  const {
    session,
    loading: authLoading,
    error: authError,
    signIn,
    signUp,
    signOut,
    refreshSession,
    setSessionDirect,
  } = useAuth();
  const [nonCriticalShellEnabled, setNonCriticalShellEnabled] = useState(false);
  const sessionUserId = session?.userId ?? null;
  const hasAuthenticatedSession = !authLoading && sessionUserId !== null;
  const location = useLocation();
  const isEntryAuthFlow = isEntryAuthFlowPath(location.pathname);
  const appShellBootstrapEnabled = shouldEnableAppShellBootstrap({
    hasAuthenticatedSession,
    isEntryAuthFlow,
  });
  const isDocumentVisible = useDocumentVisibility();
  useClientActivityMonitor({
    authLoading,
    hasSession: session !== null,
  });

  useEffect(() => {
    if (sessionUserId === null) {
      setNonCriticalShellEnabled(false);
      return;
    }
    if (!isDocumentVisible) {
      return;
    }
    if (nonCriticalShellEnabled) {
      return;
    }
    let cancelled = false;
    const idleCallback =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(() => {
            if (!cancelled) {
              setNonCriticalShellEnabled(true);
            }
          })
        : null;
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) {
        setNonCriticalShellEnabled(true);
      }
    }, 1200);
    return () => {
      cancelled = true;
      if (idleCallback !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleCallback);
      }
      window.clearTimeout(fallbackTimer);
    };
  }, [isDocumentVisible, nonCriticalShellEnabled, sessionUserId]);

  const bootstrap = useBootstrapData({
    session,
    enabled: appShellBootstrapEnabled,
  });
  const { notify } = useNotifications();
  const organisations = bootstrap.organisations;
  const accessibleOrganisationIds = useMemo(
    () => new Set(organisations.map((organisation) => organisation.id)),
    [organisations],
  );
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const liveSelectedOrganisationProjectsQuery = useProjectsQuery({
    enabled: appShellBootstrapEnabled && selectedOrganisationId !== null,
    organisationId: selectedOrganisationId,
  });
  const effectiveShellProjects = useMemo(
    () =>
      mergeShellProjects({
        bootstrapProjects: bootstrap.projects,
        liveSelectedOrganisationProjects: liveSelectedOrganisationProjectsQuery.data?.items,
      }),
    [bootstrap.projects, liveSelectedOrganisationProjectsQuery.data?.items],
  );
  const visibleProjects = useMemo(
    () =>
      resolveShellVisibleProjects({
        selectedOrganisationId,
        bootstrapVisibleProjects: bootstrap.visibleProjects,
        liveSelectedOrganisationProjects: liveSelectedOrganisationProjectsQuery.data?.items,
      }),
    [
      bootstrap.visibleProjects,
      liveSelectedOrganisationProjectsQuery.data?.items,
      selectedOrganisationId,
    ],
  );
  const accessScopedOrganisationId = useMemo(
    () =>
      resolveAccessScopedOrganisationId({
        selectedOrganisationId,
        session,
        accessibleOrganisationIds,
      }),
    [accessibleOrganisationIds, selectedOrganisationId, session],
  );
  const displayScopedOrganisationId = useMemo(
    () =>
      resolveDisplayScopedOrganisationId({
        selectedOrganisationId,
        session,
        accessibleOrganisationIds,
      }),
    [accessibleOrganisationIds, selectedOrganisationId, session],
  );
  const preserveRouteScopedWorkspaceSelection = useMemo(
    () =>
      shouldPreserveRouteScopedWorkspaceSelection({
        pathname: location.pathname,
        search: location.search,
        selectedOrganisationId,
      }),
    [location.pathname, location.search, selectedOrganisationId],
  );
  const pendingPostOnboardingOrganisationId =
    getPendingPostOnboardingState(sessionUserId)?.organisationId ?? null;
  useEffect(() => {
    if (!bootstrap.projectsLoaded) {
      return;
    }
    if (selectedOrganisationId === null) {
      return;
    }
    if (preserveRouteScopedWorkspaceSelection) {
      return;
    }
    if (
      shouldPreservePendingOnboardingSelection({
        pendingPostOnboardingOrganisationId,
        selectedOrganisationId,
      })
    ) {
      return;
    }
    if (accessibleOrganisationIds.has(selectedOrganisationId)) {
      return;
    }
    setSelectedProjectId(null);
    setSelectedOrganisationId(null);
  }, [
    accessibleOrganisationIds,
    bootstrap.projectsLoaded,
    pendingPostOnboardingOrganisationId,
    preserveRouteScopedWorkspaceSelection,
    selectedOrganisationId,
    setSelectedOrganisationId,
    setSelectedProjectId,
  ]);
  const shouldLoadAppShellEntitlements = shouldQueryAppShellEntitlements({
    isEntryAuthFlow,
    nonCriticalShellEnabled,
    projectsLoaded: bootstrap.projectsLoaded,
  });
  const entitlementsQuery = useOrganisationEntitlements(
    shouldLoadAppShellEntitlements ? displayScopedOrganisationId : null,
  );
  const entitlements = entitlementsQuery.data ?? null;
  const planStatus = useMemo(
    () =>
      resolvePlanStatus({
        entitlements,
        projects: visibleProjects.map((project) => ({
          id: project.id,
          name: project.name,
          slug: project.slug,
          badgeChars: project.badgeChars,
          badgeColor: project.badgeColor,
          badgeEnabled: project.badgeEnabled,
          createdAt: project.createdAt,
        })),
        selectedProjectId,
      }),
    [entitlements, selectedProjectId, visibleProjects],
  );
  const projectPlanStateById = useMemo(
    () => new Map(planStatus.projectOptions.map((project) => [project.id, project])),
    [planStatus.projectOptions],
  );
  const hasScheduledDowngrade =
    entitlements !== null &&
    entitlements.subscription.scheduledPlanCode !== null &&
    PLAN_PRICE_GBP_MINOR_BY_CODE[entitlements.subscription.scheduledPlanCode] <
      PLAN_PRICE_GBP_MINOR_BY_CODE[entitlements.plan.code] &&
    entitlements.subscription.scheduledPlanEffectiveAt instanceof Date &&
    entitlements.subscription.scheduledPlanEffectiveAt.getTime() > Date.now();
  const sidebarPlanName =
    entitlements !== null
      ? `${getPlanDisplayName(entitlements.plan.code)}${hasScheduledDowngrade ? '*' : ''}`
      : null;

  const projectLimit = entitlements?.plan.limits.projectLimit ?? null;
  const canCreateProject = projectLimit ? visibleProjects.length < projectLimit : true;
  const lockProjectSelector = false;

  const { routeDefinitions, featureFlags, featureFlagsLoading } = useAppFeatureFlags({
    routes: appRouteDefinitions,
    userId: sessionUserId,
    organisationId: selectedOrganisationId ?? null,
  });
  const effectiveFeatureFlags = featureFlags;
  const effectiveFlagsLoading = featureFlagsLoading;
  const navRouteDefinitions = routeDefinitions;
  const navItems = useMemo(() => getNavItemsFromRoutes(navRouteDefinitions), [navRouteDefinitions]);
  const { isAdmin: isCurrentOrgAdmin, isLoading: isCurrentOrgRoleLoading } = useCurrentOrgRole(
    session?.userId,
    {
      enabled: !isEntryAuthFlow && accessScopedOrganisationId !== null,
      organisationId: accessScopedOrganisationId,
    },
  );
  const { navItemsForSession, isAdminUser, isSuperuser } = useNavItems(session, navItems, {
    currentOrgIsAdmin: isCurrentOrgAdmin,
  });
  const canAccessAdminRoutes = isAdminUser(session);
  const adminAccess = {
    canAccess: canAccessAdminRoutes,
    loading: isCurrentOrgRoleLoading,
  };
  const isSuperuserUser = isSuperuser(session);

  const updateUserContext = useUpdateUserContext();
  const navigate = useNavigate();
  const dialogs = useAppDialogs({
    session,
    bootstrap,
    navigate: (path: string, options) => {
      void navigate(path, options);
    },
    refreshSession,
    setSessionDirect,
    updateUserContext: updateUserContext.mutateAsync,
  });

  const storedProjectId = getStoredContext().lastProjectId;
  const sessionLastOrganisationId = session?.lastOrganisationId ?? null;
  const sessionLastProjectId = session?.lastProjectId ?? null;
  const hasOpenDialogs =
    dialogs.addProjectDialog.open ||
    dialogs.addOrganisationDialog.open ||
    dialogs.feedbackDialog.open;

  return (
    <FeatureFlagsProvider flags={effectiveFeatureFlags} loading={effectiveFlagsLoading}>
      {!authBypassClerk ? (
        <ClerkOrganisationSync
          organisations={organisations}
          displayOrganisationId={displayScopedOrganisationId}
        />
      ) : null}
      <AppBootstrapController
        authBypassClerk={authBypassClerk}
        session={session}
        authLoading={authLoading}
        authError={authError}
        signIn={signIn}
        signUp={signUp}
        signOut={signOut}
        refreshSession={refreshSession}
        setSessionDirect={setSessionDirect}
        adminAccess={adminAccess}
        isSuperuser={isSuperuserUser}
        organisations={organisations}
        bootstrap={{
          projectsLoaded: bootstrap.projectsLoaded,
          projects: effectiveShellProjects,
          visibleProjectOptions: visibleProjects.map((project) => ({
            id: project.id,
            name: project.name,
            slug: project.slug,
            badgeChars: project.badgeChars,
            badgeColor: project.badgeColor,
            badgeEnabled: project.badgeEnabled,
          })),
        }}
        selectedOrganisationId={selectedOrganisationId ?? null}
        selectedProjectId={selectedProjectId ?? null}
        setSelectedOrganisationId={setSelectedOrganisationId}
        setSelectedProjectId={setSelectedProjectId}
        dialogs={dialogs}
        routeDefinitions={routeDefinitions}
        featureFlags={featureFlags}
        effectiveFeatureFlags={effectiveFeatureFlags}
        effectiveFlagsLoading={effectiveFlagsLoading}
        featureFlagsLoading={featureFlagsLoading}
        canCreateProject={canCreateProject}
        storedProjectId={storedProjectId}
        sessionLastOrganisationId={sessionLastOrganisationId}
        sessionLastProjectId={sessionLastProjectId}
        onRouteAddProject={() => {
          if (!canCreateProject) {
            notify({ message: getToastMessage('project.limitReached'), tone: 'orange' });
            return;
          }
          dialogs.openAddProject(true);
        }}
        updateUserContext={updateUserContext.mutate}
      >
        {(state) => (
          <>
            <AppShellRouter
              state={state}
              session={session}
              authError={authError}
              adminAccess={adminAccess}
              isSuperuser={isSuperuserUser}
              navItems={navItemsForSession}
              projects={visibleProjects.map((project) => ({
                id: project.id,
                name: project.name,
                slug: project.slug,
                badgeChars: project.badgeChars,
                badgeColor: project.badgeColor,
                badgeEnabled: project.badgeEnabled,
                locked: projectPlanStateById.get(project.id)?.locked ?? false,
                lockReason: projectPlanStateById.get(project.id)?.lockReason ?? null,
              }))}
              allProjects={effectiveShellProjects.map((project) => ({
                id: project.id,
                name: project.name,
                slug: project.slug,
                badgeChars: project.badgeChars,
                badgeColor: project.badgeColor,
                badgeEnabled: project.badgeEnabled,
                locked: projectPlanStateById.get(project.id)?.locked ?? false,
                lockReason: projectPlanStateById.get(project.id)?.lockReason ?? null,
              }))}
              organisations={organisations}
              canCreateProject={canCreateProject}
              planName={sidebarPlanName}
              planCode={entitlements?.plan.code ?? null}
              planAlerts={planStatus.alerts}
              planBadge={planStatus.badge}
              uploadBlockedReason={planStatus.uploadBlockedReason}
              addOns={entitlements?.addOns ?? null}
              lockProjectSelector={lockProjectSelector}
              onAddProject={() => {
                if (!canCreateProject) {
                  notify({ message: getToastMessage('project.limitReached'), tone: 'orange' });
                  return;
                }
                dialogs.openAddProject(true);
              }}
              onAddOrganisation={() => {
                dialogs.openAddOrganisation();
              }}
              onOpenFeedback={() => {
                dialogs.openFeedback();
              }}
              onSignOut={signOut}
            />
            {hasOpenDialogs ? (
              <Suspense fallback={null}>
                <AppDialogHost dialogs={{ ...dialogs, feedbackSourcePath: location.pathname }} />
              </Suspense>
            ) : null}
          </>
        )}
      </AppBootstrapController>
    </FeatureFlagsProvider>
  );
};
